import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import useConfig from 'app/config';

import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import ResourceHeader from 'app/ui/components/molecules/resource-header';
import ResourceSections from 'app/ui/components/molecules/resource-sections';
import Divider from 'app/ui/components/atoms/divider';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import { PillFieldSet, type Pills } from 'app/ui/components/molecules/pill-field-set';
import { trackEvent } from 'src/app/utils/analytics/tracker';
import { parseFiltersByLabels } from 'src/app/utils/analytics/amplitude/helpers';

import { Collection as COLLECTION } from 'app/pages/collection/query.gql';
import { lessonToWorkout } from 'app/utils/workouts';
import { routes } from 'app/routes/constants';

import type {
  CollectionQuery,
  CollectionQueryVariables,
  WorkoutListControllerQuery,
  Concept,
  Equipment,
  LessonCondition,
} from 'app/types/graphql';
import MoreResourceContent from 'src/app/ui/components/molecules/more-resource-content';

import WorkoutList from 'app/ui/components/molecules/workout-list';
import { WorkoutListController as FILTERS } from 'app/ui/components/molecules/workout-list-controller/filters.gql';
import WorkoutFilter from 'app/ui/components/molecules/workout-filter';
import { AVAILABLE_DURATION_DATA } from 'app/ui/components/molecules/workout-list-controller';
import {
  createAllPill,
  useFilterChangeHandler,
  createPillsFromData,
  useMultipleFilterChangeHandler,
} from 'app/ui/components/molecules/workout-list-controller/utils';
import { getWorkoutDurationRange } from 'src/app/ui/components/molecules/workout-list-wrapper';

const formatChallengeType = (challengeType: string): string =>
  challengeType.charAt(0).toUpperCase() + challengeType.slice(1).toLowerCase();

const BreadcrumbWrapper = styled.div`
  display: flex;
  margin-top: ${({ theme: { spacing } }) => spacing(10)};
  margin-bottom: -${({ theme: { spacing } }) => spacing(2)};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const StyledPillFieldSet = styled(PillFieldSet)`
  margin: ${({ theme: { spacing } }) => spacing(4)} 0 0;
`;

const StyledMoreResourceContent = styled(MoreResourceContent)`
  margin-top: 0;
`;

const StyledResourceSections = styled(ResourceSections)`
  margin-bottom: ${({ theme }) => theme.spacing(18)};
`;

const StyledResourceHeader = styled(ResourceHeader)`
  margin-bottom: ${({ theme }) => theme.spacing(8)};

  ${({ theme: { media } }) => `${media.medium(() => 'margin-bottom: 0;')}`};
`;

const CollectionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const { config } = useConfig();
  const resourceListsEnabled = config.REACT_APP_RESOURCE_LISTS_ENABLED;
  const challengeCollectionsEnabled = !!config.REACT_APP_ENABLE_CHALLENGE_COLLECTIONS;

  if (!id) {
    return <ErrorComponent />;
  }

  const [selectedSubCollectionId, setSelectedSubCollectionId] = useState<string | null>('all');

  const [selectedConcept, setSelectedConcept] = useState<string | null>('all-concept');
  const [selectedEquipmentPieces, setSelectedEquipmentPieces] = useState<string[]>(['all-equipment']);
  const [selectedIntensity, setSelectedIntensity] = useState<number | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string | null>('all-duration');

  const [showMoreFiltersSidebar, setShowMoreFiltersSidebar] = useState<boolean>(false);

  const [equipmentFilters, setEquipmentFilters] = useState<Pills>([]);
  const [conceptFilters, setConceptFilters] = useState<Pills>([]);
  const [durationFilters, setDurationFilters] = useState<Pills>([]);

  const { data: filtersData } = useQuery<WorkoutListControllerQuery>(FILTERS);
  const allPillText = intl.formatMessage({ id: 'filters.labels.all' });

  useEffect(() => {
    trackEvent('collection_viewed', {
      collection_id: id,
    });
  }, [id]);

  useEffect(() => {
    const allEquipmentPill = createAllPill({
      name: allPillText,
      elementName: 'equipment',
      selectedElements: selectedEquipmentPieces,
    });
    const equipmentPills = createPillsFromData({
      prependPills: [allEquipmentPill],
      data: (filtersData?.allEquipment as Equipment[]) || [],
      selectedElements: selectedEquipmentPieces,
    });

    const allConceptsPill = createAllPill({
      name: allPillText,
      elementName: 'concept',
      selectedElements: selectedConcept ? [selectedConcept] : [],
    });
    const conceptsPills = createPillsFromData({
      prependPills: [allConceptsPill],
      data: (filtersData?.allConcepts as Concept[]) || [],
      selectedElements: selectedConcept ? [selectedConcept] : [],
    });

    const allDurationsPill = createAllPill({
      name: allPillText,
      elementName: 'duration',
      selectedElements: selectedDuration ? [selectedDuration] : [],
    });
    const durationsPills = createPillsFromData({
      prependPills: [allDurationsPill],
      data: AVAILABLE_DURATION_DATA,
      selectedElements: selectedDuration ? [selectedDuration] : [],
    });

    setEquipmentFilters(equipmentPills);
    setConceptFilters(conceptsPills);
    setDurationFilters(durationsPills);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersData?.allEquipment, filtersData?.allConcepts, allPillText]);

  const handleEquipmentChange = useMultipleFilterChangeHandler({
    setFilters: setEquipmentFilters,
    setSelected: setSelectedEquipmentPieces,
    allOptionName: 'all-equipment',
  });
  const handleConceptChange = useFilterChangeHandler(setConceptFilters, setSelectedConcept);
  const handleDurationChange = useFilterChangeHandler(setDurationFilters, setSelectedDuration);

  const resetFilters = () => {
    setSelectedIntensity(null);
    handleEquipmentChange('all-equipment');
    handleConceptChange('all-concept');
    handleDurationChange('all-duration');
  };

  const lessonsCondition: LessonCondition = {
    ...(selectedConcept && selectedConcept !== 'all-concept' ? { concept: { id: selectedConcept } } : {}),
    ...(selectedIntensity ? { rpe: selectedIntensity } : {}),
    ...(selectedEquipmentPieces.length > 0 && !selectedEquipmentPieces.includes('all-equipment')
      ? { equipment: selectedEquipmentPieces.map((id) => ({ id: Number(id) })) }
      : {}),
    ...(selectedDuration && selectedDuration !== 'all-duration'
      ? { durationRange: getWorkoutDurationRange(selectedDuration) }
      : {}),
  };

  const {
    data: currentData,
    loading,
    error,
    previousData,
  } = useQuery<CollectionQuery, CollectionQueryVariables>(COLLECTION, {
    variables: { id, filter: lessonsCondition },
  });

  const data = currentData || previousData;

  const filterPills = {
    concept: conceptFilters,
    equipment: equipmentFilters,
    duration: durationFilters,
  };

  const parsedFilters = parseFiltersByLabels(lessonsCondition, filterPills);

  useEffect(() => {
    trackEvent('collection_filtered', {
      filters_applied: parsedFilters,
      results_count: data?.collection?.challenges?.length || data?.collection?.lessons?.length || 0,
      collection_id: id,
    });
  }, [id, data, parsedFilters]);

  if (loading && !data) {
    return <Loading />;
  }

  if (error || !data || !data.collection) {
    return <ErrorComponent />;
  }

  const { collection } = data;
  // A collection holds either lessons or challenges. Challenge collections render their challenges as workout cards
  const isChallengeCollection = challengeCollectionsEnabled && (collection.challenges?.length ?? 0) > 0;
  const hasSubCollections = collection.collectionList?.collections && collection.collectionList.collections.length > 0;
  const subCollections = collection.collectionList?.collections || [];

  // If no sub-collections, show lessons directly from collection
  // If sub-collections exist, show lessons from selected sub-collection, all sub-collections, or first one
  const lessonsToShow = hasSubCollections
    ? selectedSubCollectionId === 'all'
      ? subCollections.flatMap((sc) => sc.lessons || [])
      : selectedSubCollectionId
        ? subCollections.find((sc) => sc.id === selectedSubCollectionId)?.lessons || []
        : subCollections[0]?.lessons || []
    : collection.lessons || [];

  const workouts = isChallengeCollection
    ? (collection.challenges || [])
        .filter((challenge) => challenge.lesson)
        .map((challenge) => ({
          ...lessonToWorkout(challenge.lesson, { link: routes.CHALLENGE.replace(':id', challenge.id) }),
          title: challenge.title,
          intensity: formatChallengeType(challenge.challengeType),
        }))
    : lessonsToShow.map((lesson) => lessonToWorkout(lesson));

  // Create pills for sub-collections with "All" option
  const subCollectionPills: Pills = hasSubCollections
    ? [
        {
          id: 'all',
          name: intl.formatMessage({ id: 'filters.labels.all' }),
          isChecked: selectedSubCollectionId === 'all',
        },
        ...subCollections.map((sc) => ({
          id: sc.id,
          name: sc.title,
          isChecked: sc.id === selectedSubCollectionId,
        })),
      ]
    : [];

  const handlePillChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSubCollectionId(event.target.value);
  };

  return (
    <>
      <GenericPage pageTitle={`${intl.formatMessage({ id: 'library.collections' })} - ${collection.title}`}>
        <StyledResourceHeader
          title={collection.title}
          content={collection.longDescription || ''}
          imageUrl={collection.bannerImage?.signedUrl || collection.bannerImage?.url || ''}
          preTitle={intl.formatMessage({ id: 'library.collection' })}
        />
        <BreadcrumbWrapper>
          <Breadcrumb contentTitle={collection.title} />
        </BreadcrumbWrapper>
        <ContentWrapper>
          {!isChallengeCollection && hasSubCollections && subCollectionPills.length > 0 && (
            <StyledPillFieldSet variant="inline" label="" pills={subCollectionPills} onChange={handlePillChange} />
          )}

          {!isChallengeCollection && showMoreFiltersSidebar && (
            <WorkoutFilter
              equipmentFilters={equipmentFilters}
              durationFilters={durationFilters}
              conceptFilters={conceptFilters}
              intensityValue={selectedIntensity}
              handleEquipmentChange={handleEquipmentChange}
              handleIntensityChange={setSelectedIntensity}
              handleDurationChange={handleDurationChange}
              handleConceptChange={handleConceptChange}
              setShowMoreFilters={setShowMoreFiltersSidebar}
              resetFilters={resetFilters}
            />
          )}

          <WorkoutList
            workouts={workouts}
            allowMoreFilters={!isChallengeCollection}
            setShowMoreFilters={setShowMoreFiltersSidebar}
            handleConceptChange={handleConceptChange}
          />

          <Divider variant="sm" fullWidth={true} />

          {collection.resource?.sections && collection.resource.sections.length > 0 && (
            <StyledResourceSections sections={collection.resource.sections} resourceId={collection.resource.id} />
          )}

          {resourceListsEnabled && <StyledMoreResourceContent limit={3} />}
        </ContentWrapper>
      </GenericPage>
    </>
  );
};

export default CollectionPage;
